import { build_document_drawer, build_dynamic_drawer, build_flagged_drawer, build_incident_drawer, build_renewal_drawer, revalidation_reason_options, risk_tone_from_score, slugify_supplier, status_meta_from_label } from './supplier_register_helpers';
import {
  build_supplier_drawer_catalog,
  save_supplier_modal_state,
  supplier_active_drawer
} from './supplier_register_ui_helpers';

function scroll_drawer_top(vm) {
  vm.$nextTick(() => {
    const drawer_body = vm.$refs.drawerBody;
    if (drawer_body) drawer_body.scrollTop = 0;
  });
}

export const supplier_register_computed = {
  active_drawer() { return supplier_active_drawer(this); },
  active_drawer_footer_primary_actions() { const actions = this.active_drawer?.footer_actions || []; return actions.some((action) => action.position) ? actions.filter((action) => action.position === 'left') : []; },
  active_drawer_footer_actions() { const actions = this.active_drawer?.footer_actions || []; return actions.some((action) => action.position) ? actions.filter((action) => action.position !== 'left') : actions; },
  has_supplier_overlay() { return this.supplier_modal_open || this.revalidation_modal_open || (['register', 'documents', 'renewals', 'incidents', 'revalidation'].includes(this.active_tab) && !!this.active_drawer); },
  active_revalidation_copy() { return this.active_revalidation_supplier ? { title: 'Send back to OnboardOS for revalidation', subtitle: `Complyon monitors active suppliers; full re-verification runs in OnboardOS. This re-opens ${this.active_revalidation_supplier.supplier} as a re-onboarding case.` } : null; }
};

export const supplier_register_watch = { has_supplier_overlay: { immediate: true, handler(is_open) { this.set_overlay_state(is_open); } } };

export function supplier_register_created() {
  this.document_drawer_catalog = build_supplier_drawer_catalog(this.documents_rows, this.suppliers, this.drawer_catalog, (row) => this.document_drawer_key(row), build_document_drawer);
  this.renewal_drawer_catalog = build_supplier_drawer_catalog(this.renewals_rows, this.suppliers, this.drawer_catalog, (row) => this.renewal_drawer_key(row), build_renewal_drawer);
  this.incident_drawer_catalog = build_supplier_drawer_catalog(this.incidents_rows, this.suppliers, this.drawer_catalog, (row) => this.incident_drawer_key(row), build_incident_drawer);
  this.flagged_drawer_catalog = build_supplier_drawer_catalog(this.revalidation_flagged, this.suppliers, this.drawer_catalog, (row) => this.flagged_drawer_key(row), build_flagged_drawer);
}

export function supplier_register_before_unmount() { this.set_overlay_state(false); }

export const supplier_register_methods = {
  set_overlay_state(is_open) { document.body.classList.toggle('supplier-register-overlay-open', is_open); },
  set_tab(tab_id) { this.active_tab = tab_id; this.close_drawer(); this.close_supplier_modal(); this.close_revalidation_modal(); },
  document_drawer_key(row) { return slugify_supplier(`${row.supplier}-${row.document}-${row.reference}-${row.expires}`); },
  renewal_drawer_key(row) { return slugify_supplier(`${row.supplier}-${row.renewal_due}`); },
  incident_drawer_key(row) { return slugify_supplier(`${row.supplier}-${row.incident}-${row.date}`); },
  flagged_drawer_key(row) { return slugify_supplier(`${row.supplier}-flagged-register`); },
  open_drawer(drawer_key) { if (!drawer_key) return; this.supplier_modal_open = false; this.active_drawer_key = drawer_key; scroll_drawer_top(this); },
  close_drawer() { this.active_drawer_key = null; this.active_document_drawer_key = null; this.active_renewal_drawer_key = null; this.active_incident_drawer_key = null; this.active_flagged_drawer_key = null; },
  supplier_row_by_name(name) { return this.suppliers.find((supplier) => supplier.supplier === name) || null; },
  open_revalidation_modal(row) { const supplier_row = row?.supplier ? this.supplier_row_by_name(row.supplier) : row; if (!supplier_row) return; this.supplier_modal_open = false; this.revalidation_modal_open = true; this.active_revalidation_supplier = supplier_row; this.active_revalidation_reason = revalidation_reason_options[0]; },
  open_default_revalidation_modal() { this.open_revalidation_modal(this.renewals_rows[0]); },
  close_revalidation_modal() { this.revalidation_modal_open = false; this.active_revalidation_supplier = null; this.active_revalidation_reason = revalidation_reason_options[0]; },
  open_document_drawer(row) {
    const key = this.document_drawer_key(row);
    const drawer = Reflect.get(this.document_drawer_catalog, key);

    if (!drawer) {
      return;
    }

    this.supplier_modal_open = false;
    this.active_document_drawer_key = key;
    this.active_drawer_key = null;

    scroll_drawer_top(this);
  },
  open_renewal_drawer(row) {
    const key = this.renewal_drawer_key(row);
    const drawer = Reflect.get(this.renewal_drawer_catalog, key);
    if (!drawer) {
      return;
    }
    this.supplier_modal_open = false;
    this.active_renewal_drawer_key = key;
    this.active_document_drawer_key = null;
    this.active_drawer_key = null;

    scroll_drawer_top(this);
  },
  open_incident_drawer(row) {
    const key = this.incident_drawer_key(row);
    const drawer = Reflect.get(this.incident_drawer_catalog, key);

    if (!drawer) {
      return;
    }

    this.supplier_modal_open = false;
    this.active_incident_drawer_key = key;
    this.active_renewal_drawer_key = null;
    this.active_document_drawer_key = null;
    this.active_drawer_key = null;

    scroll_drawer_top(this);
  },
  open_flagged_drawer(row) {
    const key = this.flagged_drawer_key(row);
    const drawer = Reflect.get(this.flagged_drawer_catalog, key);
    if (!drawer) {
      return;
    }
    this.supplier_modal_open = false;
    this.active_flagged_drawer_key = key;
    this.active_incident_drawer_key = null;
    this.active_renewal_drawer_key = null;
    this.active_document_drawer_key = null;
    this.active_drawer_key = null;
    scroll_drawer_top(this);
  },
  open_supplier_modal() { this.close_drawer(); this.supplier_modal_open = true; },
  close_supplier_modal() { this.supplier_modal_open = false; },
  handle_drawer_action(action) { if (action?.label === 'Revalidate in OnboardOS' && this.active_drawer?.title) this.open_revalidation_modal(this.supplier_row_by_name(this.active_drawer.title)); },
  submit_revalidation_modal() { if (this.active_revalidation_supplier) this.close_revalidation_modal(); },
  save_supplier_modal() {
    const drawer_key = save_supplier_modal_state(this, {
      build_dynamic_drawer,
      risk_tone_from_score,
      slugify_supplier,
      status_meta_from_label
    });
    if (!drawer_key) return;
    this.open_drawer(drawer_key);
  }
};
